import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Siren, ChevronDown } from "lucide-react";
import { api } from "../lib/api";
import { usePersistedState } from "../lib/useSort";

function fmtDay(iso) {
  if (!iso) return "";
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function SellThroughAlerts({ outletIds }) {
  const [doc, setDoc] = useState(null);
  const [open, setOpen] = usePersistedState("bm.sellThroughOpen", true);

  useEffect(() => {
    const params = outletIds?.length ? { outlet_ids: outletIds.join(",") } : {};
    api.get("/alerts/sell-through", { params }).then((r) => setDoc(r.data)).catch(() => setDoc({ alerts: [] }));
  }, [outletIds?.join(",")]);

  const alerts = doc?.alerts || [];
  if (!doc || alerts.length === 0) return null;
  const w = doc.window || {};
  const ups = alerts.filter((a) => a.direction === "up").length;
  const downs = alerts.length - ups;

  return (
    <section className="border-2 border-[#0a0a0a] bg-[#ede8dc]" data-testid="sell-through-alerts">
      <button
        onClick={() => setOpen(!open)}
        data-testid="sell-through-toggle"
        className="w-full flex items-center justify-between gap-4 px-5 py-3 text-left hover:bg-[#0a0a0a]/5"
      >
        <span className="flex items-center gap-3 flex-wrap">
          <Siren size={14} className="text-[#c72c2c]" />
          <span className="bm-eyebrow text-[#c72c2c]">Sell-through alerts</span>
          <span className="bm-eyebrow text-neutral-600">
            {alerts.length} {alerts.length === 1 ? "category" : "categories"} moved ±{Math.round(doc.threshold_pct || 25)}% week over week
            {ups ? ` · ${ups} up` : ""}{downs ? ` · ${downs} down` : ""}
          </span>
        </span>
        <span className="flex items-center gap-3 bm-eyebrow text-neutral-500 whitespace-nowrap">
          {fmtDay(w.start)} – {fmtDay(w.end)} vs prior 7 days · own window, not the date picker
          <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && (
        <div className="border-t border-[#0a0a0a] divide-y divide-[#0a0a0a]/15" data-testid="sell-through-list">
          {alerts.map((a) => {
            const up = a.direction === "up";
            return (
              <div key={a.category} className="px-5 py-3 flex items-center justify-between gap-4 flex-wrap" data-testid={`sell-through-row-${a.category}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {up ? <TrendingUp size={16} className="text-emerald-700 shrink-0" /> : <TrendingDown size={16} className="text-[#c72c2c] shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      {a.category}
                      {a.is_printed_tees && <span className="bm-eyebrow text-neutral-400 ml-2">cohort</span>}
                    </div>
                    <div className="bm-eyebrow text-neutral-500">
                      {up ? "Selling faster" : "Slowing down"} — {a.prev_units.toLocaleString()} units last week → {a.units.toLocaleString()} this week
                    </div>
                  </div>
                </div>
                <div className={`font-mono-bm text-lg font-bold ${up ? "text-emerald-700" : "text-[#c72c2c]"}`}>
                  {up ? "+" : "−"}{Math.abs(a.pct).toFixed(0)}%
                </div>
              </div>
            );
          })}
          <div className="px-5 py-2 bm-eyebrow text-neutral-400">
            Categories with fewer than 20 units either week, and products with no sales the week before, are left out so this only flags real movement.
          </div>
        </div>
      )}
    </section>
  );
}
